<script lang="ts" setup>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import { Modal, WorkspaceTree } from "@/components";
import { RootWorkspaceFragment } from "@/graphql/operations";

const props = defineProps({
  submitting: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
  sourceItemName: {
    type: String,
    required: true,
  },
  action: {
    type: String,
    required: true,
  },
  active: {
    type: Boolean,
    required: true,
  },
});

const store = useStore();

const emit = defineEmits(["close", "submit"]);

const rootWorkspaces = computed(() =>
  store.getters.rootWorkspaces?.filter(
    (w: RootWorkspaceFragment) => w.myPrivileges?.mayAdministrate,
  ),
);

const emitClose = () => {
  if (!props.submitting) emit("close");
};

const selectedWorkspace = ref<string | undefined>(
  rootWorkspaces.value?.[0]?.id,
);
const select = (workspaceId: string) => {
  if (!props.submitting) selectedWorkspace.value = workspaceId;
};

const submit = () => {
  if (!selectedWorkspace.value || props.submitting) return;
  emit("submit", selectedWorkspace.value);
};
</script>

<template>
  <Modal
    :active="active"
    @close="emitClose"
    :close-disabled="submitting"
    class="medium scrolling"
  >
    <template v-slot:header>
      <div>
        <div>
          {{ $t(`folderPicker.header.${action}`, { name: sourceItemName }) }}
        </div>
      </div>
    </template>

    <template v-slot:body>
      <p v-if="errorMessage" role="alert" class="has-text-danger">
        {{ errorMessage }}
      </p>
      <workspace-tree
        :selected="selectedWorkspace"
        :singleWorkspace="false"
        :mustBeAdmin="true"
        @change="select"
      />
    </template>

    <template v-slot:footer>
      <div class="buttons">
        <button class="button" :disabled="submitting" @click="emitClose">
          {{ $t("folderPicker.cancel") }}
        </button>
        <button
          :class="{
            button: true,
            primary: true,
            disabled: selectedWorkspace === undefined || submitting,
          }"
          :disabled="selectedWorkspace === undefined || submitting"
          @click="submit"
        >
          {{ $t(`folderPicker.submit.${action}`) }}
        </button>
      </div>
    </template>
  </Modal>
</template>
